import { useNavigate } from "react-router";
import { border_radius } from "src/design-system/tokens/border";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import { PaneNav } from "src/domain/PaneNav/PaneNav";
import styled from "styled-components";
import { FontLoader } from "../../../components/FontLoader/FontLoader";
import { Navbar } from "../../../components/Navbar/Navbar";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { color as oldColor } from "../../../theme";
import { desktopLayout, useLayout } from "../../../utils/hooks/useLayout";
import {
  useRouteCanonical,
  useRouteIndex,
} from "../../../utils/hooks/useRoute";
import useSearch from "../../../utils/hooks/useSearch";
import { useTypedLocation } from "../../../utils/hooks/useTypedLocation";
import { hashChange } from "../../../utils/location/hashChange";
import { ScheduleLoadingRow } from "../DynamicSchedules/ScheduleLoadingRow/ScheduleLoadingRow";

export function SegmentScreenLoading() {
  const layout = useLayout();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const routeCanonical = useRouteCanonical();
  const { searchResponse } = useSearch();
  const routeIndex = useRouteIndex();
  const isMobile = layout === "mobile";

  const handleBackClick = () => {
    if (searchResponse === undefined || routeIndex === undefined) {
      // Back to search response screen
      navigate(hashChange(undefined, location));
      return;
    }

    const numSegmentsInRoute =
      searchResponse.routes[routeIndex].segments.length;

    // If only one segment in the route, don't show the route screen
    if (numSegmentsInRoute === 1) {
      navigate(hashChange(undefined, location));
    } else {
      navigate(hashChange(`#r/${routeCanonical}`, location));
    }
  };

  return (
    <LoadingWrapper>
      {isMobile ? (
        <>
          <Navbar headingText="" onBackClick={handleBackClick} />
          <HeadingWrapper isMobile={isMobile}>
            <Skeleton width="80%" height={20} padding="5px 0 0" lightOnDark />
          </HeadingWrapper>
          <DateWrapper>
            <Skeleton width="24%" height={14} margin="0 0 4px" lightOnDark />
            <Skeleton width="40%" height={14} lightOnDark />
          </DateWrapper>
        </>
      ) : (
        <HeadingWrapper isMobile={isMobile}>
          <PaneNav onBackClick={handleBackClick} />
          <Skeleton
            width="80%"
            height={24}
            margin="18px 0 14px"
            lightOnDark={false}
          />
          <Skeleton width="100%" height={46} />
        </HeadingWrapper>
      )}
      <SectionWrapper>
        <SectionHeaderLoading>
          <Skeleton width="30%" variant="h4" />
        </SectionHeaderLoading>
        <ScheduleLoadingRow transitMode="train" noBorder />
        <ScheduleLoadingRow transitMode="train" />
        <ScheduleLoadingRow transitMode="train" />
        <ScheduleLoadingRow transitMode="train" />
        <ScheduleLoadingRow transitMode="train" />
        {layout !== "mobile" && (
          <>
            <ScheduleLoadingRow transitMode="train" />
            <ScheduleLoadingRow transitMode="train" />
            <ScheduleLoadingRow transitMode="train" />
            <ScheduleLoadingRow transitMode="train" />
            <ScheduleLoadingRow transitMode="train" />
          </>
        )}
      </SectionWrapper>
      <Skeleton height={50} />
      <Skeleton height={18} width="30%" margin="18px 12px" />
      <FontLoader />
    </LoadingWrapper>
  );
}

const LoadingWrapper = styled.div`
  // Give enough space that footer ads don't creep into the view
  min-height: 200vh;
`;

const HeadingWrapper = styled.div<{
  isMobile: boolean;
}>`
  background-color: ${(props) => {
    if (props.isMobile) {
      return `${oldColor.n50}`;
    }
    return `${oldColor.n20}`;
  }};
  min-height: 55px;
  padding: 0 0 ${spacing.xl};
`;

const DateWrapper = styled.div`
  background-color: ${oldColor.n50};
  padding: ${spacing.xl};
`;

const SectionWrapper = styled.div`
  ${desktopLayout} {
    background-color: ${color.bg.fill.solid};
  }
  border-radius: ${border_radius.rounded_lg};
`;

const SectionHeaderLoading = styled.div`
  padding: ${spacing.xl};
`;
